// @flow
import * as React from "react";
import Media from "react-media";
import idx from "idx";
import { rgba } from "polished";
import { withRouter, type RouterHistory } from "react-router-dom";
import { connect } from "react-redux";
import styled, { css } from "styled-components";
import { Link as RawCommonLink } from "../../../common/Link";
import { fontSize, rtl } from "../../../common/styled-mixins";
import Gettext from "../../../common/Gettext";
import RawTimer, {
  getDuration,
  getActive,
  getCompleted,
  getNew,
  mapStateToTime
} from "../../Timer";
import PrizeFund from "./PrizeFund";
import ButtonSwitcher from "./ButtonSwitcher";
import RawLink from "../../Link";
import Sticker from "../../Sticker";

import Tournament from "../../Api";
import { ModalConsumer, type ShowModal } from "../../../common/ModalProvider";
import { JoinPopup, LeavePopup } from "../../Popups";

import type { Tournament as TournamentProps } from "../../../../redux/CommonTypes";
import background from "./images/tournament-card-bg.png";

const Container = styled.div`
  direction: ${({ theme }) => theme.direction};
  position: relative;
  display: flex;
  flex-direction: column;
  width: 200px;
  height: 228px;
  padding: 18px;
  font-size: 10px;
  line-height: 1.1;
  color: #cccccc;
  background-color: ${({ theme }) => theme.tournamentCardBg};
  background-image: linear-gradient(
      to bottom,
      ${({ theme }) => rgba(theme.tournamentCardBg, 0.75)},
      ${({ theme }) => rgba(theme.tournamentCardBg, 0.75)}
    ),
    url(${({ bg }) => (bg ? bg : background)});
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  box-shadow: inset 0px 0px 0px 1px ${({ theme }) => theme.mainDark};

  @media (min-width: 960px) {
    :nth-child(n + 3) {
      margin-top: 18px;
    }
  }

  @media (min-width: 480px) and (max-width: 959px) {
    margin: 9px;
  }

  @media (max-width: 479px) {
    width: 100%;

    :not(:first-child) {
      margin-top: 18px;
    }
  }

  ${({ participating, trading, theme }) => {
    if (trading === true || participating === true) {
      return css`
        box-shadow: inset 0px 0px 0px ${trading ? 3 : 1}px ${theme.accent};
      `;
    }
  }};

  ${({ isTournamentPage }) =>
    isTournamentPage &&
    css`
      box-shadow: none;
    `};

  ${"" /* ${({ bg }) =>
    bg &&
    css`
      background-image: linear-gradient(
          rgba(0, 0, 0, 0.45),
          rgba(0, 0, 0, 0.45)
        ),
        url(${bg});
    `}; */};
`;

const Heading = styled.div`
  min-height: 14px;
  font-weight: 700;
  font-size: ${fontSize(14)};
  line-height: 1;
  color: ${({ theme }) => theme.tournamentCardMainColor};
  ${rtl("text-align", "left")};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: color 0.3s ease;
  pointer-events: none;

  :hover,
  :focus,
  :active {
    color: ${({ theme }) => theme.alternativeAccent};
  }
`;

const HeadingLink = styled(RawCommonLink)`
  color: inherit;
  text-decoration: none;
  pointer-events: auto;
`;

const Text = styled.div`
  padding-top: 0.1em;
`;

const TakingPart = styled.div`
  color: #cccccc;
`;

const Link = styled(RawLink)`
  margin-top: 0.5em;
`;

const Timer = styled(RawTimer)`
  padding: 5px 0;
  color: ${({ theme }) =>
    theme.mode === "light" ? theme.background : theme.foreground};

  ${Heading} + & {
    margin-top: 1em;
  }
`;

type Props = {
  ...TournamentProps,
  dispatch: Function,
  history: RouterHistory,
  api: any,
  enabledStock: boolean,
  time: string,
  onClose: () => void,
  onActivateTournament: (id: number) => void
};

class Card extends React.Component<Props, {}> {
  onTradeClick = async () => {
    const { api, onClose, dispatch, id } = this.props;

    const activate = await api.fetchEntity(
      `/api/tournaments/activate?id=${id}`
    );

    if (activate !== null) {
      api.fetchEntity(`/api/tournaments/list?status=1`);
      api.fetchEntity(`/api/tournaments/info?id=${id}`);
      await api.fetchEntity(`/api/tournaments/stats`);
      dispatch({
        type: "ENGAGE_TOURNAMENT_MODE"
      });
      onClose();
    }
  };

  render() {
    const {
      api,
      onActivateTournament,
      enabledStock,
      onClose,
      isTournamentPage,
      ...props
    } = this.props;
    const active_tournament = idx(
      api.readEntity("/api/tournaments/stats"),
      stats => stats.value.active_tournament
    );

    const isParticipating = props.participation_status === 1;
    const bg = idx(props, props => props.images.bar);
    const isNew = getNew(props);
    const active = getActive(props);
    const isCompleted = getCompleted(props);
    // const bg =
    //   props.images && props.images.bar != null ? props.images.bar : null;
    const isTrading =
      active_tournament != null && active_tournament.id === props.id;

    if (isCompleted || isTrading) {
      return null;
    }

    const isAndroid = window.androidApp !== undefined;

    return (
      <Media query="(min-width: 960px)">
        {matches => (
          <Container
            participating={isParticipating}
            trading={isTrading}
            bg={bg}
            isTournamentPage={isTournamentPage}
          >
            <Heading>
              <HeadingLink to={`/tournaments/${props.id}`}>
                {props.title}
              </HeadingLink>
            </Heading>
            {isParticipating === true && (
              <TakingPart>
                <Gettext>You are participating</Gettext>
              </TakingPart>
            )}
            <Timer
              days={getDuration(props, "days")}
              hours={getDuration(props, "hours")}
              minutes={getDuration(props, "minutes")}
              seconds={getDuration(props, "seconds")}
              active={active}
              isCompleted={isCompleted}
              isParticipating={isParticipating}
              labelOnTop={true}
              small={true}
              start_date={props.start_date}
              finish_date={props.finish_date}
              toStart
            />
            <PrizeFund {...props}>
              <Text>
                <Gettext>Prize fund</Gettext>:
              </Text>
            </PrizeFund>
            <ModalConsumer>
              {({ showModal }: { showModal: ShowModal }) => (
                <ButtonSwitcher
                  active={active}
                  flat={true}
                  isCompleted={isCompleted}
                  isCardButton
                  usePlaceholders
                  onActiveTradeClick={() =>
                    isAndroid
                      ? window.androidApp.tournamentTrade(props.id)
                      : onClose
                  }
                  enabledStock={enabledStock}
                  onTradeClick={
                    isAndroid
                      ? () => window.androidApp.tournamentTrade(props.id)
                      : this.onTradeClick
                  }
                  onJoinClick={() => showModal(JoinPopup, props)}
                  onLeaveClick={() => showModal(LeavePopup, props)}
                  {...props}
                />
              )}
            </ModalConsumer>
            <Link to={`/tournaments/${props.id}`}>
              <Gettext>Learn more</Gettext>
            </Link>
            <Sticker {...props} active={active} isNew={isNew} />
          </Container>
        )}
      </Media>
    );
  }
}

export default withRouter(
  connect(mapStateToTime)(props => (
    <Tournament.Consumer>
      {api => <Card {...props} key={props.id} api={api} />}
    </Tournament.Consumer>
  ))
);



// WEBPACK FOOTER //
// ./src/components/Tournaments/Account/Card/Card.js